import { publicQuestionnaireStructure } from "./questionnaire";
/**
 * The questionnaire comparison (prototype `election-page` variant C): every
 * public candidate in one Race, question by question. Built once and opened
 * from both the Election view's Race and a Person view's campaign.
 *
 * It is state, not a destination: it opens in place as a modal dialog, never
 * changes the URL, and returns focus to whatever opened it. Its read loads
 * only when a visitor opens it, and the API cache makes re-opening free.
 */
import type { Api, PublicRaceResponses } from "../api";
import { h, uid } from "../dom";
import { RESPONSE_LABEL, responseBadge, responseClass } from "../labels";
import { personHref, personSlug } from "../links";
import { renderState } from "./states";

export interface ComparisonOptions {
  api: Api;
  page: URL;
  cycle: string;
  race: string;
  /** The Person the visitor came from, when opened from a Person view. */
  highlightPerson?: string;
  /** Public Candidacy keys leading the selected ballot; omitted on Person pages. */
  candidateKeys?: readonly string[];
  /** Called after the Retry button is pressed. */
  onRetry?: () => void;
}

function table(data: PublicRaceResponses, opts: ComparisonOptions): HTMLElement {
  const candidates = data.candidates;
  const key = uid("comparison");
  const head = h(
    "tr",
    { class: "bsc-comparison__candidates" },
    h("td", { class: "bsc-comparison__corner" }),
    candidates.map((c,index) => {
      const href = personHref(opts.page, c.publicUrl, { from: opts.cycle, race: opts.race });
      const highlight = opts.highlightPerson !== undefined && personSlug(c.publicUrl) === opts.highlightPerson;
      return h(
        "th",
        {
          scope: "col",
          id: `${key}-candidate-${index}`,
          class: `bsc-comparison__candidate${highlight ? " bsc-comparison__candidate--highlight" : ""}`,
        },
        href ? h("a", { href }, c.name) : c.name,
        highlight ? h("span", { class: "bsc-sr-only" }, " (the profile you came from)") : null,
        " ",
        responseBadge(c.status)
      );
    })
  );
  const structure = publicQuestionnaireStructure(data.sections, data.questions);
  const originalIndex = new Map(data.questions.map((q,i)=>[q.id ?? structure.inputIds[i],i]));
  const rows = structure.groups.flatMap(g => {
    const sectionId = `${key}-section-${g.section?.id}`;
    const sectionRows = g.section ? [h("tr", {class:"bsc-comparison__section"},
      h("th", {id:sectionId,colspan:candidates.length+1,scope:"colgroup"}, g.section.title))] : [];
    return [...sectionRows, ...g.questions.flatMap(({question,subQuestions})=>[question,...subQuestions].map(q=>{
      const i=originalIndex.get(q.id)!;
      const parent=structure.flatQuestions.find(parent=>parent.id===q.parentQuestionId);
      const promptId=`${key}-q${q.id}`;
      return h("tr", {class:`bsc-comparison__question${parent?" bsc-comparison__subquestion":""}`},
        h("th", {id:promptId,scope:"row",class:"bsc-comparison__prompt",headers:g.section?sectionId:null,
          "aria-label":parent?`${q.label}. ${q.text} — sub-question of ${parent.label}. ${parent.text}`:null}, q.label+". "+q.text),
        candidates.map((c,index)=>{
          const attributes={class:"bsc-comparison__answer",headers:[`${key}-candidate-${index}`,promptId,g.section?sectionId:null].filter(Boolean).join(" ")};
          if(c.status!=="completed")return h("td",{...attributes,class:`bsc-comparison__answer bsc-comparison__status bsc-status--${responseClass(c.status)}`},RESPONSE_LABEL[c.status]);
          const answer=c.answers[i];
          return !answer?.trim()?h("td",attributes,h("span",{class:"bsc-comparison__no-answer"},"No answer")):h("td",attributes,answer);
        }));
    }))];
  });
  return h(
    "div",
    { class: "bsc-comparison__scroll" },
    h(
      "table",
      { class: "bsc-comparison__table" },
      h("caption", { class: "bsc-sr-only" }, `${data.questionnaire.title}: answers by candidate`),
      h("thead", null, head),
      h("tbody", null, rows)
    )
  );
}

/** Render one Race's comparison into `container`. Fetches on call. */
export async function renderComparison(container: HTMLElement, opts: ComparisonOptions): Promise<void> {
  renderState("loading", container, { subject: "comparison" });
  const result = await opts.api.getRaceResponses(opts.cycle, opts.race);
  if (!result.ok) {
    renderState(result.reason === "not-found" ? "not-found" : "unavailable", container, {
      subject: "comparison",
      onRetry: opts.onRetry,
    });
    return;
  }
  const keys = opts.candidateKeys ? new Set(opts.candidateKeys) : null;
  const data = keys ? {...result.data, candidates: result.data.candidates.filter(c=>keys.has(c.key))} : result.data;
  const t = table(data, opts);
  t.querySelector("table")!.style.setProperty("--bsc-comparison-columns", String(data.candidates.length));
  container.replaceChildren(h("p", { class: "bsc-comparison__subtitle" }, result.data.questionnaire.title), t);
}

/**
 * Open the comparison as a modal over the view that owns `host`. Escape and
 * the Close button close it; focus returns to `trigger`.
 */
export function openComparison(
  host: HTMLElement,
  trigger: HTMLElement,
  raceName: string,
  opts: Omit<ComparisonOptions, "onRetry">
): HTMLDialogElement {
  const titleId = uid("comparison-title");
  const body = h("div", { class: "bsc-comparison__body" });
  const close = h("button", { type: "button", class: "bsc-comparison__close" }, "Close");
  const dialog = h(
    "dialog",
    { class: "bsc-comparison", "aria-labelledby": titleId, "data-race-slug": opts.race },
    h(
      "header",
      { class: "bsc-comparison__header" },
      h("div", null, h("p", { class: "bsc-comparison__eyebrow" }, "Questionnaire comparison"), h("h2", { class: "bsc-comparison__title", id: titleId }, raceName)),
      close
    ),
    body
  );
  close.addEventListener("click", () => dialog.close());
  dialog.addEventListener("close", () => {
    dialog.remove();
    if (trigger.isConnected) trigger.focus();
  });
  host.append(dialog);
  dialog.showModal();
  const load = () => void renderComparison(body, { ...opts, onRetry: load });
  load();
  return dialog;
}
